import {useEffect, useRef, useState} from "react";
import {useApi} from "./useApi";
import {mergeWatchHistory} from "../utils/mergeRemote";
import {DOB_RESOLVED_EVENT, readStoredDob} from "../../myCase/dobSession";

// State + setter come from the dashboard's single useStorage instance rather
// than a useStorage call here: a second instance would write the shared
// sessionStorage key with its own stale copy of the other slice, clobbering
// the activity saved by useActivity (and vice versa).
const useWatchHistory = ({user, watchHistory, setWatchHistory}) => {
  const {fetchWatchHistory} = useApi();
  const [historyReady, setHistoryReady] = useState(!user?.customer_id);
  const [dobEpoch, setDobEpoch] = useState(0);
  const replaceLocalRef = useRef(false);

  const historyRef = useRef(watchHistory);

  useEffect(() => {
    historyRef.current = watchHistory;
  }, [watchHistory]);

  useEffect(() => {
    const customerId = user?.customer_id;
    if (!customerId) return undefined;
    const onResolved = (e) => {
      if (e?.detail?.customerId && e.detail.customerId !== customerId) return;
      // A newly matched household member must not inherit the previous
      // participant's local entries via mergeWatchHistory.
      replaceLocalRef.current = true;
      setDobEpoch((n) => n + 1);
    };
    window.addEventListener(DOB_RESOLVED_EVENT, onResolved);
    return () => window.removeEventListener(DOB_RESOLVED_EVENT, onResolved);
  }, [user?.customer_id]);

  useEffect(() => {
    if (!user?.customer_id) {
      setHistoryReady(true);
      return undefined;
    }

    let cancelled = false;
    setHistoryReady(false);
    const replaceLocal = replaceLocalRef.current;
    replaceLocalRef.current = false;
    const dob = readStoredDob(user.customer_id);
    fetchWatchHistory(user.customer_id, dob)
      .then((remote) => {
        if (cancelled) return;
        setWatchHistory((local) =>
          replaceLocal
            ? remote && typeof remote === "object"
              ? remote
              : {}
            : mergeWatchHistory(local, remote),
        );
        setHistoryReady(true);
      })
      .catch(() => {
        // Leave historyReady false so App keeps the login snapshot instead
        // of treating a failed request as an authoritative empty history.
      });
    return () => {
      cancelled = true;
    };
    // Keyed on the participant, not the user object: App replaces that object
    // for unrelated changes (e.g. the avatar URL), which refetched this.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.customer_id, dobEpoch]);

  return {historyRef, historyReady};
};
export default useWatchHistory;
